import React, { useState } from "react";
import TextField from '@material-ui/core/TextField'
import Snackbar from '@material-ui/core/Snackbar';
import styled from "styled-components";

import { Alert } from '../components/Alert'
import { AlertType } from '../models'
import { pushNotification } from '../utils/api.utils'
import {
	Flex,
	SectionWrapper,
	FormCard,
	FieldGroup,
	Label,
	ActionToolbar,
	ToolbarButton,
	SecondaryHeader
} from '../styles/global'

interface Props {
	code: string;
}

export const URLNotification = ({ code }: Props) => {
	const [notifTitle, setNotifTitle] = useState<string>("Title");
	const [notifURL, setNotifURL] = useState<string>("https://epidemica.info/");
	const [notifDesc, setNotifDesc] = useState<string>("");
	const [notifArgs, setNotifArgs] = useState<string>("");
	const [alertMessage, setAlertMessage] = useState<string>('');
	const [alertVisible, setAlertVisible] = useState<boolean>(false);
	const [alertSeverity, setAlertSeverity] = useState<AlertType>(AlertType.SUCCESS);

	const handleAlertClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === 'clickaway') {
			return;
		}

		setAlertVisible(false);
		setAlertMessage("")
		setAlertSeverity(AlertType.SUCCESS)
	};

	return (
		<div style={{ animation: 'fadeIn 0.3s ease-out' }}>
			<Snackbar open={alertVisible} autoHideDuration={6000} onClose={handleAlertClose}>
				<Alert onClose={handleAlertClose} severity={alertSeverity}>
					{alertMessage}
				</Alert>
			</Snackbar>

			<SectionWrapper>
				<SecondaryHeader>Push URL Notification</SecondaryHeader>
				<FormCard style={{ maxWidth: '800px' }}>
					<FieldGroup>
						<Label>Notification Title</Label>
						<TextField
							fullWidth
							variant="outlined"
							placeholder="e.g. New Update Available"
							value={notifTitle}
							onChange={(e) => setNotifTitle(e.target.value)}
						/>
					</FieldGroup>

					<FieldGroup>
						<Label>Description</Label>
						<TextField
							fullWidth
							variant="outlined"
							placeholder="e.g. Please check the latest guidelines..."
							value={notifDesc}
							onChange={(e) => setNotifDesc(e.target.value)}
						/>
					</FieldGroup>

					<Flex gap="24px">
						<FieldGroup style={{ flex: 1 }}>
							<Label>Target URL</Label>
							<TextField
								fullWidth
								variant="outlined"
								placeholder="https://..."
								value={notifURL}
								onChange={(e) => setNotifURL(e.target.value)}
							/>
						</FieldGroup>

						<FieldGroup style={{ flex: 1 }}>
							<Label>Custom Arguments (Optional)</Label>
							<TextField
								fullWidth
								variant="outlined"
								placeholder="e.g. type=info"
								value={notifArgs}
								onChange={(e) => setNotifArgs(e.target.value)}
							/>
						</FieldGroup>
					</Flex>

					<InfoHelpBox>
						<InfoHelpIcon>ℹ</InfoHelpIcon>
						<InfoHelpContent>
							<InfoHelpTitle>Target URL Options</InfoHelpTitle>
							<InfoHelpText>
								In the <strong>Target URL</strong> field, separate multiple URLs with <code>@$@</code> to randomly select one when opened. Custom Arguments are appended to each URL as query parameters.
							</InfoHelpText>
							<InfoHelpExample>
								https://epidemica.info/?type=info<code>@$@</code>https://epidemica.info/?type=survey
							</InfoHelpExample>
							<InfoHelpNote>URLs must be valid (parseable by the app) or they will be ignored.</InfoHelpNote>
						</InfoHelpContent>
					</InfoHelpBox>

					<ActionToolbar style={{ borderTop: 'none', paddingTop: 0, marginTop: 16 }}>
						<ToolbarButton
							variant="contained"
							color="primary"
							disabled={!notifTitle || !notifURL}
							onClick={() => {
								pushNotification(code, notifTitle, notifDesc, notifURL, notifArgs)
									.then((response: any) => {
										setAlertMessage("Notification Pushed Successfully")
										setAlertVisible(true)
										return response
									})
									.catch((err: any) => {
										console.error(err);
										setAlertMessage("Failed to push notification");
										setAlertSeverity(AlertType.ERROR);
										setAlertVisible(true);
									})
							}}
						>
							Push Notification
						</ToolbarButton>
					</ActionToolbar>
				</FormCard>
			</SectionWrapper>
		</div>
	);
};

const InfoHelpBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  border: 1px solid #c7d2fe;
  border-radius: 10px;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const InfoHelpIcon = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  background: #6366f1;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  margin-top: 2px;
`;

const InfoHelpContent = styled.div`
  flex: 1;
`;

const InfoHelpTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #4338ca;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 6px;
`;

const InfoHelpText = styled.p`
  font-size: 14px;
  color: #4c4f69;
  line-height: 1.5;
  margin: 0;
  
  code {
    display: inline;
    padding: 2px 6px;
    background: #6366f1;
    color: white;
    border-radius: 4px;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
    font-size: 12px;
    font-weight: 600;
  }
`;

const InfoHelpExample = styled.div`
  font-size: 12px;
  color: #64748b;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
  background: rgba(99, 102, 241, 0.08);
  padding: 10px 12px;
  border-radius: 6px;
  margin-top: 10px;
  word-break: break-all;
  
  code {
    display: inline;
    padding: 2px 4px;
    background: #6366f1;
    color: white;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 600;
  }
`;

const InfoHelpNote = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: 10px 0 0 0;
  font-style: italic;
`;
